import { useState, type ReactNode } from "react";
import { RotateCcw, SlidersHorizontal } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export interface ColumnGroup<K extends string> {
  key: string;
  /** Group heading; null for columns that stand on their own. */
  label: string | null;
  columns: { key: K; label: string }[];
}

/**
 * Columns that are off until the user picks them (a list's label columns),
 * shown in the order they were picked.
 */
export interface OptInColumns {
  label: string;
  columns: string[];
  shown: string[];
  onToggle: (column: string) => void;
}

/**
 * A "Columns" button opening a checklist of the table's columns, grouped.
 * Hidden state is the caller's: it decides where it is remembered.
 */
export default function ColumnPickerMenu<K extends string>({
  groups,
  hidden,
  onChange,
  defaultHidden,
  optIn,
  footnote,
  onReset,
}: {
  groups: ColumnGroup<K>[];
  hidden: K[];
  onChange: (hidden: K[]) => void;
  defaultHidden: K[];
  optIn?: OptInColumns;
  footnote?: ReactNode;
  /** Replaces the default reset (back to `defaultHidden`). */
  onReset?: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const toggle = (key: K) =>
    onChange(hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key]);
  const toggleGroup = (group: ColumnGroup<K>) => {
    const keys = group.columns.map((c) => c.key);
    const allHidden = keys.every((k) => hidden.includes(k));
    onChange(
      allHidden ? hidden.filter((k) => !keys.includes(k)) : [...new Set([...hidden, ...keys])],
    );
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<SlidersHorizontal fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        Columns
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          {groups.map((group) => {
            const keys = group.columns.map((c) => c.key);
            const shown = keys.filter((k) => !hidden.includes(k)).length;
            return (
              <Box key={group.key} sx={{ "& + &": { mt: 1.5 } }}>
                {group.label ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={shown === keys.length}
                        indeterminate={shown > 0 && shown < keys.length}
                        onChange={() => toggleGroup(group)}
                      />
                    }
                    label={
                      <Typography variant="subtitle2" component="span">
                        {group.label}
                      </Typography>
                    }
                  />
                ) : null}
                <Stack sx={{ pl: group.label ? 3 : 0 }}>
                  {group.columns.map((c) => (
                    <FormControlLabel
                      key={c.key}
                      control={
                        <Checkbox
                          size="small"
                          checked={!hidden.includes(c.key)}
                          onChange={() => toggle(c.key)}
                        />
                      }
                      label={c.label}
                    />
                  ))}
                </Stack>
              </Box>
            );
          })}
          {optIn && optIn.columns.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {optIn.label}
              </Typography>
              <Stack>
                {optIn.columns.map((column) => (
                  <FormControlLabel
                    key={column}
                    control={
                      <Checkbox
                        size="small"
                        checked={optIn.shown.includes(column)}
                        onChange={() => optIn.onToggle(column)}
                      />
                    }
                    label={column}
                  />
                ))}
              </Stack>
            </Box>
          )}
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {footnote ?? "Remembered for you"}
            </Typography>
            <Button
              size="small"
              startIcon={<RotateCcw fontSize="small" />}
              onClick={onReset ?? (() => onChange([...defaultHidden]))}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
